import notificationService from "services/notification.service";
import { create } from "zustand";

const useNotification = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loadingNotification: false,
  notificationError: null,
  eventSource: null,

  async getNotifications() {
    set({ loadingNotification: true });
    try {
      const response = await notificationService.getAll();
      set({ notifications: response.data });
    } catch (error) {
      set({ notificationError: error.message });
    } finally {
      set({ loadingNotification: false });
    }
  },

  async getUnreadCount() {
    try {
      const response = await notificationService.getUnreadCount();
      set({ unreadCount: response.data });
    } catch (error) {
      set({ notificationError: error.message });
    }
  },

  async markAsRead(uuid) {
    try {
      await notificationService.markAsRead(uuid);
      const { notifications, unreadCount } = get();
      const updatedNotifications = notifications.map(n =>
        n.uuid === uuid ? { ...n, isRead: 1 } : n
      );
      set({
        notifications: updatedNotifications,
        unreadCount: Math.max(0, unreadCount - 1),
      });
    } catch (error) {
      set({ notificationError: error.message });
    }
  },

  async markAllAsRead() {
    try {
      await notificationService.markAllAsRead();
      const { notifications } = get();
      const updatedNotifications = notifications.map(n => ({
        ...n,
        isRead: 1,
      }));
      set({ notifications: updatedNotifications, unreadCount: 0 });
    } catch (error) {
      set({ notificationError: error.message });
    }
  },

  async deleteNotification(uuid) {
    try {
      await notificationService.delete(uuid);
      const { notifications, unreadCount } = get();
      const notificationToDelete = notifications.find(n => n.uuid === uuid);
      const updatedNotifications = notifications.filter(n => n.uuid !== uuid);
      
      // Se a notificação deletada não estava lida, decrementar o contador
      const newUnreadCount = notificationToDelete && !notificationToDelete.isRead
        ? Math.max(0, unreadCount - 1)
        : unreadCount;
      
      set({
        notifications: updatedNotifications,
        unreadCount: newUnreadCount,
      });
    } catch (error) {
      set({ notificationError: error.message });
    }
  },

  addNotification(notification) {
    const { notifications, unreadCount } = get();
    set({
      notifications: [notification, ...notifications],
      unreadCount: unreadCount + 1,
    });
  },

  initEventSource() {
    const { eventSource } = get();
    if (eventSource) {
      return; // Já está conectado
    }

    const es = notificationService.createEventSource();

    es.onmessage = event => {
      try {
        const notification = JSON.parse(event.data);
        get().addNotification(notification);
      } catch (error) {
        console.error("Erro ao processar notificação:", error);
      }
    };

    es.onerror = error => {
      console.error("Erro no EventSource:", error);
      es.close();
      set({ eventSource: null });
      // Tentar reconectar após 5 segundos
      setTimeout(() => {
        get().initEventSource();
      }, 5000);
    };

    set({ eventSource: es });
  },

  closeEventSource() {
    const { eventSource } = get();
    if (eventSource) {
      eventSource.close();
      set({ eventSource: null });
    }
  },

  clearNotificationError() {
    set({ notificationError: null });
  },
}));

export default useNotification;
